import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export interface IProfessionalModifierFormData {
  modifier: string;
  multiplier: number;
  providerId: string | null;
  payerId: number | null;
  cptCodes: string[];
  groupName: string;
  isDefault: boolean;
}

export const INITIAL_PROFESSIONAL_MODIFIER_FORM_DATA: IProfessionalModifierFormData =
  {
    modifier: "",
    multiplier: 1,
    providerId: null,
    payerId: null,
    cptCodes: [],
    groupName: "",
    isDefault: false,
  };

interface IProfessionalModifierFormProps {
  title: string;
  submitLabel: string;
  formData: IProfessionalModifierFormData;
  onChange: (data: IProfessionalModifierFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** In edit mode, `isDefault` is the discriminator for scope and is locked. */
  scopeLocked?: boolean;
}

export function ProfessionalModifierForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IProfessionalModifierFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    const base = (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
    return [{ label: "All payers", value: null }, ...base];
  }, [payers]);

  const handleChange = <K extends keyof IProfessionalModifierFormData>(
    field: K,
    value: IProfessionalModifierFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    formData.modifier.trim().length > 0 &&
    (formData.isDefault || formData.providerId !== null) &&
    !isSubmitting;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="modifier">Modifier</label>
            <InputText
              id="modifier"
              value={formData.modifier}
              onChange={(e) =>
                handleChange("modifier", e.target.value.toUpperCase())
              }
              placeholder="e.g. 80, 50, AS"
            />
          </Field>
          <Field>
            <label htmlFor="multiplier">Multiplier</label>
            <InputNumber
              inputId="multiplier"
              value={formData.multiplier}
              onValueChange={(e) => handleChange("multiplier", e.value ?? 1)}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              min={0}
              max={99}
            />
            <FieldHelp>0.16 = 16% of allowable, 1.5 = 150%</FieldHelp>
          </Field>
        </FieldGrid>

        <CheckboxLine>
          <Checkbox
            inputId="isDefault"
            checked={formData.isDefault}
            disabled={scopeLocked}
            onChange={(e) => handleChange("isDefault", !!e.checked)}
          />
          <label htmlFor="isDefault">
            Default rule (applies to all providers and payers unless overridden)
          </label>
        </CheckboxLine>

        {!formData.isDefault && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply this multiplier to
                every payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="cptCodes">CPT Codes</label>
            <Chips
              inputId="cptCodes"
              value={formData.cptCodes}
              onChange={(e) => handleChange("cptCodes", e.value ?? [])}
              placeholder="96523, J1642, 0272-0372"
              separator=","
            />
            <FieldHelp>Leave empty to apply to all CPT codes.</FieldHelp>
          </Field>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional label shown in pricing breakdowns"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
